import AlwaysDiscountCouponPageView from 'src/pages/AlwaysDiscounCouponPage/view'
import usePageNav from 'src/hooks/usePageNav.hook'
import React, { Dispatch, SetStateAction, useCallback, useEffect } from 'react'
import { categoryListQueryOption, useCustomQuery } from 'src/hooks/react-query/useCustomQuery.hook'
import { AlwaysDiscountCouponListSpace, CategoryListSpace, ResponseDataType } from 'src/types/api'
import {
  alwaysCouponListQueryOption,
  useInfiniteScrollQuery,
} from 'src/hooks/react-query/useCustomInfiniteQuery.hook'
import { QueryClient } from '@tanstack/react-query'

import RenderIf from 'src/components/common/RenderIf'
import { Loading } from 'src/components/common/Loading'
import useParamsType from 'src/hooks/useParamsType.hook'
import { listItemsType } from 'src/components/common/CustomSelect'
import { useGlobalStore } from 'src/store/zustand'

export type AlwaysPageCouponSortOrderType = 0 | 1 | 2 | 3 | 4

export interface AlwaysDiscountCouponPageProps {
  setTarget: Dispatch<SetStateAction<HTMLDivElement | null | undefined>>
  alwaysCouponListData?: AlwaysDiscountCouponListSpace.CommonCouponType[]
  categoryData?: CategoryListSpace.CategoryInfoType[] | undefined
  alwaysProductsNoMore: boolean
  alwaysCouponDetailPageNav: (couponNumber: string, couponIdx: number) => void
  alwaysCategory: string
  isAlwaysQueryCouponListSuccess?: boolean
  selectListItems: listItemsType<AlwaysPageCouponSortOrderType>[]
  couponSortOrder: AlwaysPageCouponSortOrderType
  alwaysCouponSortHandler: (value: AlwaysPageCouponSortOrderType) => void
}
const skeletonDefaultData = {
  couponList: Array(12).fill({
    CouponIdx: 0,
    BrandName: '',
    BrandNumber: 0,
    CouponImage: '',
    CouponName: '',
    CouponNumber: '',
    DiscountRate: 0,
    RegularPrice: 0,
    SalePrice: 0,
  }),
}

const selectListItems: listItemsType<AlwaysPageCouponSortOrderType>[] = [
  { text: '인기순', value: 4 },
  { text: '최근등록순', value: 3 },
  { text: '높은할인순', value: 0 },
]

export const alwaysDiscountCouponPageLoader = (queryClient: QueryClient) => async () => {
  return Promise.allSettled([await queryClient.ensureQueryData(categoryListQueryOption())])
}

const AlwaysDiscountCouponPage = () => {
  const { alwaysCategory } = useParamsType()
  const { alwaysCouponDetailPageNav } = usePageNav()
  const { data: categoryQueryData = { Body: { CategoryInfo: [] } }, isLoading: isCategoryLoading } =
    useCustomQuery<CategoryListSpace.Response>(categoryListQueryOption())
  const {
    alwaysSelectSortOrder: couponSortOrder,
    setAlwaysSelectSortOrder: setCouponSortOrder,
    setAlwaysSelectCategory,
  } = useGlobalStore()

  const {
    data: alwaysQueryCouponListQueryData,
    setTarget,
    isSuccess: isAlwaysQueryCouponListSuccess,
  } = useInfiniteScrollQuery<
    AlwaysDiscountCouponListSpace.Request,
    AlwaysDiscountCouponListSpace.Response
  >(alwaysCouponListQueryOption(couponSortOrder, Number(alwaysCategory)))

  const alwaysCouponListProcessData = useCallback(() => {
    let result: AlwaysDiscountCouponListSpace.CommonCouponType[] | any[] = []
    alwaysQueryCouponListQueryData?.pages?.map(
      (element: ResponseDataType<AlwaysDiscountCouponListSpace.Response>) => {
        !!element?.Body?.CommonCoupon && result.push(...element?.Body?.CommonCoupon)
      },
    )
    return {
      result,
      noMore: alwaysQueryCouponListQueryData?.pages?.at(-1)?.Body?.CommonCoupon?.length === 0,
    }
  }, [alwaysQueryCouponListQueryData])

  useEffect(() => {
    if (alwaysCategory !== '0') setAlwaysSelectCategory(Number(alwaysCategory))
  }, [alwaysCategory])

  const props: AlwaysDiscountCouponPageProps = {
    categoryData: categoryQueryData?.Body?.CategoryInfo,
    alwaysCouponListData: isAlwaysQueryCouponListSuccess
      ? alwaysCouponListProcessData().result
      : skeletonDefaultData.couponList,
    alwaysProductsNoMore: alwaysCouponListProcessData().noMore,
    setTarget,
    alwaysCategory,
    alwaysCouponDetailPageNav,
    isAlwaysQueryCouponListSuccess,
    selectListItems,
    couponSortOrder,
    alwaysCouponSortHandler: (value: AlwaysPageCouponSortOrderType) => setCouponSortOrder(value),
  }

  return (
    <React.Fragment>
      <RenderIf when={isCategoryLoading}>
        <Loading.Default />
      </RenderIf>
      <AlwaysDiscountCouponPageView {...props} />
    </React.Fragment>
  )
}
export default AlwaysDiscountCouponPage
